import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cx } from '../../lib/util/cx';

/**
 * Port of the DS `Select` (native select + chevron). Handoff rule: a filter
 * select's placeholder means "all" — pass `placeholder` and it becomes the
 * empty-value first option.
 */
export type SelectOption = string | { value: string; label: string };

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: SelectOption[];
  placeholder?: string;
  surface?: 'cream' | 'white';
  /** Compact height for filter bars (the kit's selects sit beside sm buttons). */
  compact?: boolean;
}

const CHEVRON =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%232A1609' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")";

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, surface = 'cream', compact = false, disabled, className, style, value, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      disabled={disabled}
      value={value ?? ''}
      className={cx(
        'w-full cursor-pointer appearance-none rounded-input border border-[rgba(143,68,32,.12)] pl-3.5 pr-9 font-body text-ink',
        'focus:border-orange focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-orange disabled:cursor-not-allowed',
        compact ? 'py-2 text-sm' : 'py-[13px] text-[15px]',
        disabled ? 'bg-[var(--hp-gray-100)]' : surface === 'white' ? 'bg-white' : 'bg-cream',
        className,
      )}
      style={{
        backgroundImage: CHEVRON,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        ...style,
      }}
      {...rest}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => {
        const opt = typeof o === 'string' ? { value: o, label: o } : o;
        return (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        );
      })}
    </select>
  );
});
